import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import './PresenceDemo.css';
import GalaxyGateWidget from '../components/galaxy/GalaxyGateWidget';
import MetricsPanel from '../components/galaxy/MetricsPanel';
import GenericHelmet from '../components/genericHelmet/GenericHelmet';
import { SocketContext } from '../context/socket';
import { AuthContext } from '../context/AuthContext';
import { SOCKET_URL } from '../socketConfig';
import useGalaxyGateEngine from '../components/galaxy/hooks/useGalaxyGateEngine';
import { usePresence } from '../context/PresenceContext';
import GlobalPresence from '../components/presence/GlobalPresence';
import '../components/presence/globalPresence.css';
import useGuestEnergy from '../components/presence/useGuestEnergy';

const httpSocketBase = (url) =>
  (url || '')
    .replace(/^ws(s)?:\/\//i, (m, s) => `http${s ? 's' : ''}://`)
    .replace(/\/$/, '');

export default function PresenceDemo() {
  const { socket } = useContext(SocketContext) || {};
  const { player } = useContext(AuthContext);
  const [metrics, setMetrics] = useState(null);
  const [surge, setSurge] = useState(null);
  const heartbeatRef = useRef(null);
  const [playerStats, setPlayerStats] = useState({ online: 0, idle: 0, offline: 0 });
  const sessionStatusRef = useRef(new Map()); // sessionId -> { playerId, status, lastSeen }
  const playerStatusRef = useRef(new Map()); // playerId -> { status, lastSeen }
  const offlineCacheRef = useRef(new Map()); // pid -> lastSeen for offline (recent)
  const prevSeenRef = useRef(new Set());
  const presencePreferredRef = useRef(false);
  const prevPresenceRef = useRef({});
  const engine = useGalaxyGateEngine(); // reuse live guest signals for panel
  const OFFLINE_WINDOW = 45 * 60 * 1000; // 45 minutes
  const { presenceMap } = usePresence() || {};
  const isDebug = process.env.NODE_ENV !== 'production';
  const log = (...args) => {
    // if (isDebug) {
    //   // eslint-disable-next-line no-console
    //   console.log('[presence-demo]', ...args);
    // }
  };

  const getPid = useCallback((obj) => {
    if (!obj) return null;
    return (
      obj.handle ||
      obj.playerId ||
      obj.player_id ||
      obj.mongoId ||
      obj._id ||
      null
    );
  }, []);

  const normalizeStatus = useCallback((status) => {
    if (!status) return 'offline';
    const s = String(status).toLowerCase();
    if (s === 'online') return 'online';
    if (s === 'idle' || s === 'away' || s === 'background') return 'idle';
    return 'offline';
  }, []);

  const recomputeCounts = useCallback(() => {
    if (presencePreferredRef.current) return; // PresenceContext is driving; skip fallback
    const now = Date.now();
    const perPlayer = new Map(); // pid -> { status, lastSeen }
    const seen = new Set();

    // From active sessions
    sessionStatusRef.current.forEach((val) => {
      if (!val?.playerId) return;
      const current = perPlayer.get(val.playerId) || { status: 'offline', lastSeen: 0 };
      const incomingStatus = normalizeStatus(val.status);
      const ls = val.lastSeen || now;
      if (!current.lastSeen || ls >= current.lastSeen) {
        perPlayer.set(val.playerId, { status: incomingStatus, lastSeen: ls });
      }
    });

    // Merge with last known player statuses (presence:player snapshot/update)
    playerStatusRef.current.forEach((entry, pid) => {
      const lastSeen = entry.lastSeen || now;
      const age = now - lastSeen;
      if (age > OFFLINE_WINDOW) return; // too old, drop it
      const status = normalizeStatus(entry.status);
      const existing = perPlayer.get(pid) || { status: 'offline', lastSeen: 0 };
      if (!existing.lastSeen || lastSeen >= existing.lastSeen) {
        perPlayer.set(pid, { status, lastSeen });
      }
    });

    const nextPlayerStatus = new Map();
    const counts = { online: 0, idle: 0, offline: 0 };
    const onlineIds = [];
    const idleIds = [];

    perPlayer.forEach((entry, pid) => {
      const lastSeen = entry.lastSeen || now;
      const age = now - lastSeen;
      if (age > OFFLINE_WINDOW) return; // prune
      const status = normalizeStatus(entry.status);
      seen.add(pid);

      if (status === 'online') counts.online += 1;
      else if (status === 'idle') counts.idle += 1;
      else counts.offline += 1; // offline (recent)

      nextPlayerStatus.set(pid, {
        status,
        lastSeen,
      });
    });

    // Incorporate offline cache (recent removals)
    const offlineCache = new Map();
    offlineCacheRef.current.forEach((ts, pid) => {
      const age = now - ts;
      if (age <= OFFLINE_WINDOW) offlineCache.set(pid, ts);
    });
    // Clear cache entries for players we just saw online/idle
    seen.forEach((pid) => {
      offlineCache.delete(pid);
    });
    counts.offline += offlineCache.size;

    // Add players that vanished since last run into offline cache
    const missing = new Set(prevSeenRef.current || []);
    seen.forEach((pid) => missing.delete(pid));
    missing.forEach((pid) => {
      if (!offlineCache.has(pid)) offlineCache.set(pid, now);
    });
    counts.offline = offlineCache.size;

    prevSeenRef.current = new Set(seen);
    offlineCacheRef.current = offlineCache;

    playerStatusRef.current = nextPlayerStatus;
    setPlayerStats(counts);
    const snapshot = Array.from(perPlayer.entries()).map(([pid, v]) => ({
      pid,
      status: v.status,
      lastSeen: v.lastSeen,
      ageSec: Number(((now - (v.lastSeen || now)) / 1000).toFixed(1)),
    }));
    log('recomputeCounts', {
      counts,
      perPlayerSize: perPlayer.size,
      entries: snapshot,
    });
    log('recomputeCounts:entries', snapshot);
  }, [OFFLINE_WINDOW]);

  // Prefer the local PresenceContext (same source as OrbitingFriends) for instant updates
  useEffect(() => {
    presencePreferredRef.current = false; // rollback: let socket-driven path handle counts
  }, [presenceMap, normalizeStatus, OFFLINE_WINDOW, getPid]);

  // Minimal guest heartbeat so the demo self-registers when unauthenticated
  useEffect(() => {
    if (!socket) return undefined;
    if (player?.token) {
      if (!socket.connected) socket.connect();
      return undefined; // avoid double-counting authed players as guests
    }

    const referrer = document.referrer || window.location.href || null;
    const guestAuth = { guest: 'true', referrer };
    const nextAuth = { ...(socket.auth || {}), ...guestAuth };
    Object.keys(nextAuth).forEach((k) => {
      if (nextAuth[k] === null || nextAuth[k] === '') delete nextAuth[k];
    });
    // ensure no lingering token for this guest view
    if (nextAuth.token) delete nextAuth.token;
    socket.auth = nextAuth;
    if (!socket.connected) socket.connect();

    const emitHeartbeat = (activityType = 'active') => {
      if (!socket.connected || !socket.id) return;
      socket.emit('presence:heartbeat', {
        guest: true,
        sessionId: socket.id,
        activityType,
      });
    };

    const start = () => {
      if (heartbeatRef.current) return;
      heartbeatRef.current = setInterval(() => {
        const vis =
          document.visibilityState === 'visible' ? 'active' : 'background';
        emitHeartbeat(vis);
      }, 10_000);
      emitHeartbeat(
        document.visibilityState === 'visible' ? 'active' : 'background',
      );
    };
    const stop = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
    const onConnect = () => start();
    const onDisconnect = () => stop();

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    window.addEventListener('beforeunload', stop);
    start();

    return () => {
      stop();
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      window.removeEventListener('beforeunload', stop);
    };
  }, [socket, player?.token]);

  // Metrics fetch every 5s from socket backend
  useEffect(() => {
    if (!socket) return undefined;
    let cancelled = false;
    const base =
      httpSocketBase(process.env.REACT_APP_SOCKET_URL || SOCKET_URL) || '';
    const url = base ? `${base}/presence/guests/metrics` : null;
    if (!url) return undefined;

    const fetchMetrics = async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setMetrics(data || null);
      } catch {
        /* ignore metric fetch failures */
      }
    };

    fetchMetrics();
    const id = setInterval(fetchMetrics, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [socket]);

  // Track player presence (lightweight counts only; view-only)
  useEffect(() => {
    if (!socket) return undefined;

    const handleSnapshot = (payload) => {
      const sessions = payload?.sessions || {};
      sessionStatusRef.current = new Map();
      Object.values(sessions).forEach((s) => {
        const sid = s.sessionId || s.session_id || s._id;
        const pid = getPid(s);
        if (!sid || !pid) return;
        const lastSeen =
          s.updatedAt || s.lastSeenAt || s.lastSeen || payload?.timestamp || Date.now();
        sessionStatusRef.current.set(sid, {
          playerId: pid,
          status: s.status || 'offline',
          lastSeen,
        });
      });
      // also include any player snapshot info if present
      const players = payload?.players || payload?.data;
      if (players) {
        playerStatusRef.current = new Map();
        const entries = Array.isArray(players)
          ? players
          : Object.values(players);
        entries.forEach((p) => {
          const pid = getPid(p);
          if (!pid) return;
          playerStatusRef.current.set(pid, {
            status: p.status || 'offline',
            lastSeen: p.lastSeenAt || p.updatedAt || payload?.timestamp || Date.now(),
          });
        });
      }
      recomputeCounts();
      log('handleSnapshot', {
        sessions: Object.keys(sessions).length,
        players: players ? (Array.isArray(players) ? players.length : Object.keys(players).length) : 0,
      });
    };

    const handlePlayerSnapshot = (payload) => {
      const players = payload?.players || payload?.data || {};
      playerStatusRef.current = new Map();
      const entries = Array.isArray(players)
        ? players
        : Object.entries(players).map(([, v]) => v);
      entries.forEach((p) => {
        const pid = getPid(p);
        if (!pid) return;
        playerStatusRef.current.set(pid, {
          status: normalizeStatus(p.status),
          lastSeen:
            p.lastSeenAt || p.updatedAt || p.timestamp || Date.now(),
        });
      });
      recomputeCounts();
      log('handlePlayerSnapshot', entries.length);
    };

    const handleDiff = (payload) => {
      const add = payload?.add || {};
      const update = payload?.update || {};
      const remove = payload?.remove || [];
      Object.values(add).forEach((s) => {
        const sid = s.sessionId || s.session_id || s._id;
        const pid = getPid(s);
        if (!sid || !pid) return;
        const lastSeen =
          s.updatedAt || s.lastSeenAt || s.lastSeen || payload?.timestamp || Date.now();
        const status = normalizeStatus(s.status || s.presenceStatus);
        sessionStatusRef.current.set(sid, {
          playerId: pid,
          status,
          lastSeen,
        });
        // fresh online/idle should clear offline cache for this player
        if (status === 'online' || status === 'idle') {
          offlineCacheRef.current.delete(pid);
        }
        log('handleDiff:add', { sid, pid, status });
      });
      Object.values(update).forEach((s) => {
        const sid = s.sessionId || s.session_id || s._id;
        const pid = getPid(s);
        if (!sid || !pid) return;
        const lastSeen =
          s.updatedAt || s.lastSeenAt || s.lastSeen || payload?.timestamp || Date.now();
        const status = normalizeStatus(
          s.status ||
            s.presenceStatus ||
            sessionStatusRef.current.get(sid)?.status ||
            'offline',
        );
        sessionStatusRef.current.set(sid, {
          playerId: pid,
          status,
          lastSeen,
        });
        if (status === 'online' || status === 'idle') {
          offlineCacheRef.current.delete(pid);
        }
        log('handleDiff:update', { sid, pid, status });
      });
      remove.forEach((sid) => {
        const entry = sessionStatusRef.current.get(sid);
        if (entry?.playerId) {
          // mark player as recently offline
          playerStatusRef.current.set(entry.playerId, {
            status: 'offline',
            lastSeen: Date.now(),
          });
          offlineCacheRef.current.set(entry.playerId, Date.now());
        }
        sessionStatusRef.current.delete(sid);
      });
      recomputeCounts();
      log('handleDiff', {
        add: Object.keys(add).length,
        update: Object.keys(update).length,
        remove: remove.length,
        sessionSize: sessionStatusRef.current.size,
      });
    };

    const handleRemoveDetail = (payload) => {
      // payload.sessionId or payload.sessionIds
      const ids = []
        .concat(payload?.sessionId || [])
        .concat(payload?.sessionIds || [])
        .concat(Array.isArray(payload) ? payload : []);
      ids.forEach((sid) => {
        const entry = sessionStatusRef.current.get(sid);
        if (entry?.playerId) {
          playerStatusRef.current.set(entry.playerId, {
            status: 'offline',
            lastSeen: Date.now(),
          });
          offlineCacheRef.current.set(entry.playerId, Date.now());
        }
        sessionStatusRef.current.delete(sid);
      });
      recomputeCounts();
      log('handleRemoveDetail', ids.length);
    };

    const handleRemoveLegacy = (payload) => {
      if (!Array.isArray(payload)) return;
      payload.forEach((sid) => {
        const entry = sessionStatusRef.current.get(sid);
        if (entry?.playerId) {
          playerStatusRef.current.set(entry.playerId, {
            status: 'offline',
            lastSeen: Date.now(),
          });
          offlineCacheRef.current.set(entry.playerId, Date.now());
        }
        sessionStatusRef.current.delete(sid);
      });
      recomputeCounts();
      log('handleRemoveLegacy', payload.length);
    };

    const handleUpdate = (payload) => {
      // presence:update payloads for single player/session
      if (!payload) return;
      const sid = payload.sessionId || payload.session_id || payload._id;
      const pid = getPid(payload);
      if (!sid || !pid) return;
      const lastSeen =
        payload.updatedAt || payload.lastSeenAt || payload.lastSeen || payload.timestamp || Date.now();
     sessionStatusRef.current.set(sid, {
       playerId: pid,
       status:
          normalizeStatus(
            payload.status ||
              payload.presenceStatus ||
              sessionStatusRef.current.get(sid)?.status ||
              'offline',
          ),
       lastSeen,
     });
     if (sessionStatusRef.current.get(sid)?.status === 'online' || sessionStatusRef.current.get(sid)?.status === 'idle') {
       offlineCacheRef.current.delete(pid);
     }
     recomputeCounts();
      log('handleUpdate', {
        sid,
        pid,
        status: sessionStatusRef.current.get(sid)?.status,
      });
    };

    const handlePlayer = (payload) => {
      const pid = getPid(payload);
      if (!pid) return;
      playerStatusRef.current.set(pid, {
        status: normalizeStatus(payload.status),
        lastSeen: payload.timestamp || Date.now(),
      });
      offlineCacheRef.current.delete(pid);
      recomputeCounts();
      log('handlePlayer', { pid, status: payload.status });
    };

    socket.on('presence:snapshot', handleSnapshot);
    socket.on('presence:diff', handleDiff);
    socket.on('presence:update', handleUpdate);
    socket.on('presence:remove:detail', handleRemoveDetail);
    socket.on('presence:remove', handleRemoveLegacy);
    socket.on('presence:player', handlePlayer);
    socket.on('player-snapshot', handlePlayerSnapshot);
    return () => {
      socket.off('presence:snapshot', handleSnapshot);
      socket.off('presence:diff', handleDiff);
      socket.off('presence:update', handleUpdate);
      socket.off('presence:remove:detail', handleRemoveDetail);
      socket.off('presence:remove', handleRemoveLegacy);
      socket.off('presence:player', handlePlayer);
      socket.off('player-snapshot', handlePlayerSnapshot);
    };
  }, [socket, getPid, recomputeCounts]);

  // Poll current players from presence backend (socket base) as a safety net
  useEffect(() => {
    if (!socket) return undefined;
    let cancelled = false;
    const httpBase =
      httpSocketBase(process.env.REACT_APP_SOCKET_URL || SOCKET_URL) || '';
    const url = httpBase ? `${httpBase}/presence/players` : null;
    if (!url) return undefined;

    const fetchPlayers = async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const players = data?.players || {};
        const next = new Map();
        const entries = Array.isArray(players)
          ? players
          : Object.entries(players).map(([, v]) => v);
        entries.forEach((p) => {
          const pid = getPid(p);
          if (!pid) return;
          next.set(pid, {
            status: p.status || 'offline',
            lastSeen:
              p.lastSeenAt || p.updatedAt || p.timestamp || Date.now(),
          });
        });
        playerStatusRef.current = next;
        if (!cancelled) {
          recomputeCounts();
        }
      } catch {
        /* ignore */
      }
    };

    fetchPlayers();
    const id = setInterval(fetchPlayers, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [socket, getPid, recomputeCounts]);

  // Listen for surge event
  useEffect(() => {
    if (!socket) return undefined;
    const handleSurge = (payload) => {
      setSurge({ ...(payload || {}), at: Date.now() });
      setTimeout(() => setSurge(null), 2500);
    };
    socket.on('guest:surge', handleSurge);
    return () => socket.off('guest:surge', handleSurge);
  }, [socket]);

  const combined = useMemo(() => {
    if (!metrics) return null;
    return {
      liveCount: metrics.liveCount ?? metrics.count ?? engine.liveCount,
      unique24h: metrics.unique24h ?? engine.unique24h,
      uniqueAllTime: metrics.uniqueAllTime ?? engine.uniqueAllTime,
      trafficSources: metrics.sources || engine.trafficSources,
      regionStats: metrics.regions || engine.regionStats,
      deviceStats: metrics.devices || engine.deviceStats,
      lastArrivals: engine.lastArrivals,
      playerStats,
    };
  }, [metrics, engine, playerStats]);

  // Derive a shared energy scalar from liveCount (used for GP core pulse)
  const liveCount = combined?.liveCount || engine?.liveCount || 0;
  const energy = useGuestEnergy({
    liveCount,
    playerStats: combined?.playerStats || engine?.playerStats,
  });
  const [soundsEnabled, setSoundsEnabled] = useState(false);
  const [ctaExpanded, setCtaExpanded] = useState(true);
  const location = useLocation();
  const registerHref = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    params.set('intent', 'presence-demo');
    const query = params.toString();
    return query ? `/register?${query}` : '/register?intent=presence-demo';
  }, [location.search]);
  const toggleCta = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setCtaExpanded((prev) => !prev);
  }, []);

  return (
    <>
      <GenericHelmet
        title="JG Presence Engine Live Demo â€” Real-Time Presence in Action"
        description="Live demo of the JG Presence Engine showing real-time sessions, lifecycle transitions, and global presence updating instantly."
      />
      <div className="presence-demo-container">
        <div className="presence-demo-header">
          <div className="presence-demo-header-inner">
            <div className="presence-demo-header-left">
              <div className="presence-demo-title">Joe Galaxy Presence Engine</div>
              <div className="presence-demo-subtitle">
                Live guest & player presence
              </div>
              <Link
                to="/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="presence-demo-docs-link"
                style={{
                  fontSize: 12,
                  opacity: 0.7,
                  color: '#c7cedd',
                  textDecoration: 'none',
                  marginTop: 4,
                  display: 'inline-flex',
                }}
              >
                Docs
              </Link>
              <div
                className="presence-demo-microcopy"
                title="Sessions updating in real time"
                style={{
                  fontSize: 12,
                  opacity: 0.82,
                  marginTop: 4,
                  pointerEvents: 'auto',
                }}
              >
                Live system state - sessions update continuously.
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginTop: 6,
                  opacity: 0.7,
                  pointerEvents: 'auto',
                  color: '#c7cedd',
                  fontWeight: 500,
                }}
              >
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={soundsEnabled}
                    onChange={(e) => setSoundsEnabled(e.target.checked)}
                  />
                  Ambient presence sounds
                </label>
              </div>
            </div>
            <div className="presence-demo-header-right">
              <div
                className={`presence-demo-cta${ctaExpanded ? '' : ' presence-demo-cta--collapsed'}`}
              >
                <Link to={registerHref} className="presence-demo-cta__link">
                  <span className="presence-demo-cta__title">
                    Participate in the live presence system
                  </span>
                  <span className="presence-demo-cta__subtext">
                    Explore live presence behavior and evaluate how it could work in your system.
                  </span>
                  <span className="presence-demo-cta__note">
                    Takes about 30 seconds. No commitment.
                  </span>
                  <span className="presence-demo-cta__explainer">
                    You’ll create a JG Universe account, then land in the presence setup path.
                  </span>
                </Link>
                <button
                  type="button"
                  className="presence-demo-cta__toggle"
                  onClick={toggleCta}
                  aria-expanded={ctaExpanded}
                  aria-label={ctaExpanded ? 'Collapse call to action' : 'Expand call to action'}
                >
                  <span aria-hidden="true">{ctaExpanded ? '▾' : '▸'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Calm idle state when no live sessions are present */}
        {liveCount === 0 && (
          <div
            style={{
              marginTop: 8,
              marginBottom: 4,
              fontSize: 13,
              opacity: 0.7,
              textAlign: 'center',
            }}
          >
            System idle - awaiting live sessions
          </div>
        )}

        {/* Global presence topology inspired by OrbitingFriends (anonymous, system-level) */}
        <GlobalPresence
          selfId={player?.data?.player?._id}
          liveCount={liveCount}
          seedCount={liveCount}
          playerStats={combined?.playerStats || engine?.playerStats}
          energy={energy}
          soundEnabled={soundsEnabled}
        />

        <div className="presence-demo-widget">
          <GalaxyGateWidget mode="cosmic" />
        </div>

        <MetricsPanel
          data={combined || engine}
          surge={surge || (engine.surgeDetected ? { level: 1 } : null)}
        />
        <div className={`presence-demo-cta-panel metrics-panel ${ctaExpanded ? '' : 'collapsed'}`}>
          <button
            className="metrics-toggle"
            type="button"
            onClick={toggleCta}
            aria-label="Toggle participate panel"
            aria-expanded={ctaExpanded}
          >
            {ctaExpanded ? '▼' : '▲'} Participate
          </button>
          {ctaExpanded && (
            <div className="metrics-body presence-demo-cta-panel-body">
              <Link to={registerHref} className="presence-demo-cta-panel-link">
                <span className="presence-demo-cta__title">
                  Participate in the live presence system
                </span>
                <span className="presence-demo-cta__subtext">
                  Explore live presence behavior and evaluate how it could work in your system.
                </span>
                <span className="presence-demo-cta__note">
                  Takes about 30 seconds. No commitment.
                </span>
                <span className="presence-demo-cta__explainer">
                  You’ll create a JG Universe account, then land in the presence setup path.
                </span>
              </Link>
            </div>
          )}
        </div>
        {surge && (
          <div className="presence-demo-surge">
            TRAFFIC SURGE DETECTED (Level {surge.level || 1})
          </div>
        )}
      </div>
    </>
  );
}




